import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useThemeMode } from '@/shared/theme/useThemeMode';
import type { ThemePref } from '@/shared/theme/themeModeContext';

export interface ThemeToggleProps {
  /** Drawer mini-variant — icon-only trigger, label as hover tooltip. */
  readonly collapsed?: boolean;
}

const ICONS: Record<ThemePref, typeof LightModeIcon> = {
  system: SettingsBrightnessIcon,
  light: LightModeIcon,
  dark: DarkModeIcon,
};

const THEME_LABELS: Record<ThemePref, string> = {
  system: 'System theme',
  light: 'Light theme',
  dark: 'Dark theme',
};

const NEXT_HINT: Record<ThemePref, string> = {
  system: 'Switch to light theme',
  light: 'Switch to dark theme',
  dark: 'Follow system theme',
};

/**
 * Sidebar theme-mode control. Click cycles `system -> light -> dark ->
 * system` via `useThemeMode().cycle()`. Rendered in `AppShell` as a plain
 * flex sibling directly above `SidebarPersonaCard` (between the existing
 * `Divider` and the persona card) — no second `Divider` is added between the
 * two, and the outer padding matches `SidebarPersonaCard`'s
 * (`px: collapsed ? 0.5 : 1`) so both controls' edges align at every drawer
 * width.
 *
 * Collapsed-rail behavior deliberately mirrors `NavItem.tsx`'s simple
 * two-branch pattern (`collapsed ? <Tooltip>{button}</Tooltip> : button`),
 * NOT `SidebarPersonaCard`'s single-stable-JSX-branch pattern. That pattern
 * exists specifically to protect an open `Menu`'s `anchorEl` from a remount
 * when `collapsed` toggles mid-open; this toggle has no menu/popover to
 * protect — it's a stateless click-to-cycle button — so `NavItem`'s simpler
 * ternary is the correct precedent here.
 */
export const ThemeToggle = ({ collapsed = false }: ThemeToggleProps): JSX.Element => {
  const { pref, resolved, cycle } = useThemeMode();
  const Icon = ICONS[pref];
  // While following the OS, say which way that resolved — "System" alone
  // doesn't tell you what you're actually looking at.
  const label = pref === 'system' ? `System · ${resolved}` : THEME_LABELS[pref].replace(' theme', '');

  const button = (
    <ButtonBase
      onClick={cycle}
      aria-label={`${THEME_LABELS[pref]}. ${NEXT_HINT[pref]}`}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: collapsed ? 0 : 1.5,
        py: 1,
        borderRadius: 1,
        justifyContent: collapsed ? 'center' : 'flex-start',
        color: 'text.secondary',
        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
      }}
    >
      <Icon fontSize="small" sx={{ flexShrink: 0 }} data-testid={`theme-toggle-icon-${pref}`} />
      {!collapsed ? (
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        >
          {label}
        </Typography>
      ) : null}
    </ButtonBase>
  );

  return (
    <Box sx={{ px: collapsed ? 0.5 : 1 }}>
      {collapsed ? (
        <Tooltip title={label} placement="right" arrow>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </Box>
  );
};
